import {
  decodePrivateBridge,
  bridgeDeploymentFor,
  bridgeRouteValue,
} from "./privateBridgeConfig";
import { ethers } from "ethers";
import {
  recoverFlatJoinSplitV2SwapOutput,
  type FlatJoinSplitV2SwapOutputSeed,
} from "./swapOutput";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementDisclosed,
  archiveFlatJoinSplitV2UndisclosedPreparation,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { assertNotAborted, fail } from "./common";
import type { OperationLog } from "./operationTypes";
import { LEAF_EVENT, POOL_INTERFACE } from "./poolAccess";
import {
  assertJournaledWalletSubmissionNotAborted,
  sendJournaledWalletTransaction,
  walletSettlementErrorWithContext,
} from "./walletSubmission";

export function progressLog(onLog: OperationLog) {
  const labels: Record<string, string> = {
    "resolving-key": "Preparing browser proof…",
    "fetching-key": "Loading browser proving data…",
    "verifying-key-hash": "Checking browser proving data…",
    "validating-key": "Validating browser proving data…",
    "loading-cached-key": "Loading saved browser proving data…",
    "caching-key": "Saving browser proving data…",
    proving: "Creating private proof in your browser…",
    "verifying-proof": "Checking private proof…",
  };
  const reported = new Set<string>();
  return (event: { phase: string; arityId: number; detail?: string }) => {
    // Only fixed stage labels reach the activity log. Worker details can
    // include URLs; witnesses and arbitrary error data must never be logged.
    const label =
      event.phase === "resolving-key" && event.detail === "fresh-worker-retry"
        ? "Restarting browser proof after a browser error…"
        : labels[event.phase];
    if (label && !reported.has(label)) {
      onLog(label, "info");
      reported.add(label);
    }
  };
}

export function attachOutputLocations(
  receipt: ethers.TransactionReceipt,
  pool: string,
  outputs: JoinSplitNote[],
): JoinSplitNote[] {
  const locations = new Map<string, { treeIndex: number; leafIndex: number }>();
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== pool.toLowerCase()) continue;
    try {
      const parsed = POOL_INTERFACE.parseLog({
        topics: [...log.topics],
        data: log.data,
      });
      if (!parsed || parsed.name !== LEAF_EVENT.name) continue;
      locations.set(String(parsed.args.leaf).toLowerCase(), {
        treeIndex: Number(parsed.args.treeIndex),
        leafIndex: Number(parsed.args.leafIndex),
      });
    } catch {
      // Other pool events are intentionally ignored.
    }
  }
  return outputs.map((note) => {
    const location = locations.get(note.commitment.toLowerCase());
    if (!location) fail(`receipt omitted output commitment ${note.commitment}`);
    if (location.treeIndex !== note.treeIndex) {
      fail("receipt inserted an output into an unexpected tree");
    }
    return { ...note, leafIndex: location.leafIndex };
  });
}

/** Stop foreground waits without erasing recovery or trusting a late response. */
function awaitSettlement<T>(
  pending: Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (!signal) return pending;
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener("abort", abort);
      reject(
        new DOMException(
          "Settlement tracking was cancelled; recovery is retained",
          "AbortError",
        ),
      );
    };
    signal.addEventListener("abort", abort, { once: true });
    pending.then(
      (value) => {
        signal.removeEventListener("abort", abort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", abort);
        reject(error);
      },
    );
    if (signal.aborted) abort();
  });
}

/** Read back a relayed hash to verify its calldata once it has propagated. */
async function fetchRelayedTransaction(
  provider: ethers.Provider,
  hash: string,
  signal?: AbortSignal,
): Promise<ethers.TransactionResponse> {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    assertNotAborted(signal, "while confirming the relayed settlement");
    const found = await awaitSettlement(provider.getTransaction(hash), signal);
    if (found) return found;
    await awaitSettlement(
      new Promise((resolve) => setTimeout(resolve, 1_000)),
      signal,
    );
  }
  return fail(
    `relayer reported ${hash} but it did not appear on chain; recover from the settlement journal before retrying`,
  );
}

export async function sendSimulated(
  signer: ethers.Signer,
  provider: ethers.Provider,
  // `value` is `bigint` because that is what the SDK's request type is now:
  // privateBridge forwards callValue. None of the entrypoints routed through
  // here does, so the zero is asserted below instead of being spelled as a
  // literal type the SDK no longer produces.
  transaction: {
    to: string;
    data: `0x${string}`;
    value: bigint;
    chainId: bigint;
  },
  pool: string,
  outputs: JoinSplitNote[],
  onLog: OperationLog,
  expectedSignerAddress: string,
  prepareJournal: (signedIntent: {
    to: string;
    data: `0x${string}`;
    value: bigint;
    chainId: bigint;
  }) => Promise<{ id: string; scope: FlatJoinSplitV2JournalScope }>,
  relay: boolean,
  signal?: AbortSignal,
  swapOutputSeed?: FlatJoinSplitV2SwapOutputSeed,
  confirmationProvider: ethers.Provider = provider,
  submitPrivateFees?: () => Promise<{ transactionHash: string }>,
  bridgeGasPayer?: string,
): Promise<{ txHash: string; outputs: JoinSplitNote[]; journalId: string }> {
  assertNotAborted(signal, "before simulation");
  const relaySignal = relay ? signal : undefined;
  // `privateBridge` forwards `callValue`; every other entrypoint is
  // non-payable and the SDK builders emit a zero there. A relayed submission
  // carries no ETH of the user's, so a value-bearing bridge must be
  // self-signed — the bridge operation refuses that combination up front, and
  // this is the backstop.
  const bridge = bridgeGasPayer && decodePrivateBridge(transaction.data);
  const payableBridge =
    !!bridge &&
    !bridge.recovery &&
    relay &&
    !!submitPrivateFees &&
    ethers.getAddress(transaction.to) ===
      bridgeDeploymentFor(bridge.provider)?.coordinator &&
    transaction.chainId === 84532n &&
    transaction.value === bridgeRouteValue(bridge.provider, bridge.route) &&
    bridge.route.sourcePool.toLowerCase() === pool.toLowerCase() &&
    ethers.isAddress(bridgeGasPayer!);
  if (relay && transaction.value !== 0n && !payableBridge) {
    fail("a relayed settlement cannot carry value");
  }
  const zeroValueRequest = {
    to: transaction.to,
    data: transaction.data,
    value: transaction.value,
    chainId: transaction.chainId,
  } as const;
  const [initialNetwork, initialSignerAddress, confirmationNetwork] =
    await awaitSettlement(
      Promise.all([
        provider.getNetwork(),
        signer.getAddress(),
        confirmationProvider === provider
          ? null
          : confirmationProvider.getNetwork(),
      ]),
      relaySignal,
    );
  if (
    BigInt(initialNetwork.chainId) !== transaction.chainId ||
    (confirmationNetwork &&
      BigInt(confirmationNetwork.chainId) !== transaction.chainId) ||
    ethers.getAddress(initialSignerAddress) !==
      ethers.getAddress(expectedSignerAddress)
  ) {
    fail("wallet account or chain changed before recovery backup");
  }
  // eth_call discloses a copyable spend proof. Persist its private outputs
  // before simulation, not only before the eventual wallet/relayer send.
  const journal = await prepareJournal(zeroValueRequest);
  let submittedHash: string | undefined;
  const walletSubmission = {
    journal,
    signal,
    onArchiveError: (error: unknown) =>
      onLog(
        `Wallet cancelled the request; its active recovery record was retained: ${error instanceof Error ? error.message : String(error)}`,
        "warning",
      ),
  };
  try {
    if (relay) assertNotAborted(signal, "before settlement disclosure");
    else await assertJournaledWalletSubmissionNotAborted(walletSubmission);
    await markFlatJoinSplitV2SettlementDisclosed(journal.scope, journal.id);
    assertNotAborted(signal, "before settlement disclosure");
    onLog("Checking transaction…", "info");
    // Use the app's current chain view for preflight as well as confirmation.
    // An injected wallet RPC can lag behind a newly issued signed fee quote and
    // reject it as not yet valid. Keep wallet network/account checks below.
    const simulated = await awaitSettlement(
      payableBridge
        ? confirmationProvider
            .call({
              ...zeroValueRequest,
              from: bridgeGasPayer,
            })
            .then(() => zeroValueRequest)
        : simulateFlatJoinSplitV2Transaction(
            confirmationProvider as any,
            zeroValueRequest,
          ),
      relaySignal,
    );
    // A relayed proof is copyable once preflight has received it. Cancellation
    // after this boundary must retain the active recovery record and inputs.
    if (relay) assertNotAborted(signal, "after settlement disclosure");
    else await assertJournaledWalletSubmissionNotAborted(walletSubmission);
    // The wallet is asked to sign the simulated call, so anything the simulation
    // changed about it has to be caught here rather than at signature time.
    if (
      ethers.getAddress(simulated.to) !== ethers.getAddress(transaction.to) ||
      simulated.data.toLowerCase() !== transaction.data.toLowerCase() ||
      simulated.value !== transaction.value ||
      simulated.chainId !== transaction.chainId
    ) {
      fail("simulation returned a different transaction than the one built");
    }
    const [finalNetwork, finalSignerAddress] = await awaitSettlement(
      Promise.all([provider.getNetwork(), signer.getAddress()]),
      relaySignal,
    );
    if (
      BigInt(finalNetwork.chainId) !== transaction.chainId ||
      ethers.getAddress(finalSignerAddress) !==
        ethers.getAddress(expectedSignerAddress)
    ) {
      fail("wallet account or chain changed after journal persistence");
    }
    let response: ethers.TransactionResponse;
    if (relay) {
      assertNotAborted(signal, "before relayer submission");
      onLog("Submitting through the relayer", "info");
      // `user_address` and `fee_details` are deliberately omitted. The flat pool
      // binds fee and feeRecipient into the proof, so the relayer establishes it
      // is paid from the calldata plus an on-chain feeRecipient() read; the
      // wallet address would add nothing to that and would link the account to
      // the withdrawal, which is the linkage relaying exists to avoid.
      const relayed = await awaitSettlement(
        submitPrivateFees
          ? submitPrivateFees()
          : submitBasePoolRelay({
              chainId: Number(simulated.chainId),
              serializedTransaction: serializeTransaction({
                to: simulated.to,
                data: simulated.data,
                value: "0x0",
              }),
              ...(signal ? { signal } : {}),
            }),
        relaySignal,
      );
      if (!/^0x[0-9a-fA-F]{64}$/.test(relayed.transactionHash)) {
        fail("relayer returned a malformed transaction hash");
      }
      submittedHash = relayed.transactionHash;
      const submitted = await fetchRelayedTransaction(
        confirmationProvider,
        relayed.transactionHash,
        signal,
      );
      // The relayer is untrusted: confirm it broadcast the settlement that was
      // simulated rather than any other call.
      if (
        !submitted.to ||
        ethers.getAddress(submitted.to) !== ethers.getAddress(simulated.to) ||
        submitted.data.toLowerCase() !== simulated.data.toLowerCase() ||
        submitted.value !== simulated.value ||
        BigInt(submitted.chainId) !== simulated.chainId
      ) {
        fail(
          "relayer submitted a transaction different from the simulated settlement",
        );
      }
      response = submitted;
    } else {
      onLog("Requesting wallet signature", "info");
      response = await sendJournaledWalletTransaction({
        ...walletSubmission,
        signer,
        transaction: {
          to: simulated.to,
          data: simulated.data,
          value: simulated.value,
          chainId: simulated.chainId,
        },
      });
      if (response && /^0x[0-9a-fA-F]{64}$/.test(response.hash))
        submittedHash = response.hash;
      if (
        !response ||
        !/^0x[0-9a-fA-F]{64}$/.test(response.hash) ||
        !response.to ||
        ethers.getAddress(response.to) !== ethers.getAddress(simulated.to) ||
        ethers.getAddress(response.from) !==
          ethers.getAddress(expectedSignerAddress) ||
        response.data.toLowerCase() !== simulated.data.toLowerCase() ||
        response.value !== simulated.value ||
        BigInt(response.chainId) !== simulated.chainId
      ) {
        fail(
          "wallet submitted a transaction different from the simulated settlement",
        );
      }
    }
    try {
      await markFlatJoinSplitV2SettlementBroadcast(
        journal.scope,
        journal.id,
        response.hash,
      );
    } catch (error) {
      onLog(
        `Settlement was submitted, but its journal hash update is pending recovery: ${
          error instanceof Error ? error.message : String(error)
        }`,
        "warning",
        response.hash,
      );
    }
    const confirmations = EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS;
    const confirmationStarted = performance.now();
    onLog(
      `Settlement submitted; waiting for ${confirmations} confirmation${confirmations === 1 ? "" : "s"}`,
      "info",
      response.hash,
    );
    const waitForReceipt = async (count: number) => {
      try {
        return await response.wait(count);
      } catch (error: any) {
        if (
          error?.code === "TRANSACTION_REPLACED" &&
          error?.cancelled === false &&
          error?.receipt
        ) {
          return error.receipt as ethers.TransactionReceipt;
        }
        throw error;
      }
    };
    const includedReceipt = await awaitSettlement(
      waitForReceipt(1),
      relaySignal,
    );
    if (!includedReceipt || includedReceipt.status !== 1) {
      fail("settlement transaction reverted");
    }
    onLog(
      `Settlement inclusion verified after ${Math.round(performance.now() - confirmationStarted)}ms of confirmation tracking`,
      "info",
      includedReceipt.hash,
    );
    try {
      await markFlatJoinSplitV2SettlementIncluded(journal.scope, journal.id, {
        txHash: includedReceipt.hash,
        blockNumber: includedReceipt.blockNumber,
        blockHash: includedReceipt.blockHash,
      });
    } catch (error) {
      onLog(
        `Settlement inclusion journal update will be recovered from chain: ${
          error instanceof Error ? error.message : String(error)
        }`,
        "warning",
        includedReceipt.hash,
      );
    }
    const receipt =
      confirmations <= 1
        ? includedReceipt
        : await awaitSettlement(
            confirmationProvider.waitForTransaction(
              includedReceipt.hash,
              confirmations,
            ),
            relaySignal,
          );
    if (!receipt || receipt.status !== 1)
      fail("settlement transaction reverted");
    if (receipt.hash.toLowerCase() !== response.hash.toLowerCase()) {
      const replacement = await awaitSettlement(
        confirmationProvider.getTransaction(receipt.hash),
        relaySignal,
      );
      if (
        !replacement?.to ||
        ethers.getAddress(replacement.to) !==
          ethers.getAddress(transaction.to) ||
        replacement.data.toLowerCase() !== transaction.data.toLowerCase() ||
        replacement.value !== transaction.value ||
        BigInt(replacement.chainId) !== transaction.chainId
      ) {
        fail(
          "replacement transaction does not match the recorded settlement intent",
        );
      }
    }
    const canonicalBlock = await awaitSettlement(
      confirmationProvider.getBlock(receipt.blockNumber),
      relaySignal,
    );
    if (
      !canonicalBlock?.hash ||
      canonicalBlock.hash.toLowerCase() !== receipt.blockHash.toLowerCase()
    ) {
      fail("settlement receipt was displaced by a chain reorganization");
    }
    const located = attachOutputLocations(receipt, pool, outputs);
    let swapOutputNote: JoinSplitNote | undefined;
    if (swapOutputSeed) {
      try {
        swapOutputNote = await awaitSettlement(
          recoverFlatJoinSplitV2SwapOutput({
            provider: confirmationProvider,
            receipt,
            pool,
            chainId: transaction.chainId,
            calldata: transaction.data,
            seed: swapOutputSeed,
          }),
          relaySignal,
        );
        const after = await awaitSettlement(
          confirmationProvider.getBlock(receipt.blockNumber),
          relaySignal,
        );
        if (after?.hash?.toLowerCase() !== receipt.blockHash.toLowerCase()) {
          fail("swap receipt changed during output recovery");
        }
      } catch (error) {
        throw Object.assign(
          new Error(
            `Swap settled; output recovery is pending in the encrypted journal: ${error instanceof Error ? error.message : String(error)}`,
          ),
          { txHash: receipt.hash, journalId: journal.id },
        );
      }
    }
    try {
      await markFlatJoinSplitV2SettlementFinalized(journal.scope, journal.id, {
        txHash: receipt.hash,
        blockNumber: receipt.blockNumber,
        blockHash: receipt.blockHash,
        outputNotes: located,
        swapOutputNote,
      });
    } catch (error) {
      onLog(
        `Settlement finalized; journal finality will be recovered from chain: ${
          error instanceof Error ? error.message : String(error)
        }`,
        "warning",
        receipt.hash,
      );
    }
    onLog(
      `Settlement confirmation tracking completed in ${Math.round(performance.now() - confirmationStarted)}ms`,
      "info",
      receipt.hash,
    );
    onLog("Fresh-pool settlement finalized", "success", receipt.hash);
    return {
      txHash: receipt.hash,
      outputs: swapOutputNote ? [...located, swapOutputNote] : located,
      journalId: journal.id,
    };
  } catch (error) {
    await archiveFlatJoinSplitV2UndisclosedPreparation(
      journal.scope,
      journal.id,
    ).catch(() => {});
    throw walletSettlementErrorWithContext(error, journal.id, submittedHash);
  }
}
